import {
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import DateRange from "../../App/Components/DateRange/DateRange";
import LoadingSpinner from "../../App/Components/LoadingSpinner/LoadingSpinner";
import Breadcrumbs from "../../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../../App/Components/PageTitle/PageTitle";
import { MessageDeliveryType } from "../../App/Enums/Message";
import { useManagementClients } from "../../App/Queries/ClientQueries";
import { useGetMessagingErrors, useGetMessagingMetrics } from "../../App/Queries/MessageQueries";
import { useAuthToken } from "../../App/State/Hooks/authHooks";
import { MessagingError, MessagingMetrics } from "../../App/Typings/MessageText";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

export default function AdminMessaging() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const [startDate, setStartDate] = useState<Date>(yesterday);
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedClient, setSelectedClient] = useState<string>("All");
  const token = useAuthToken();
  const navigate = useNavigate();
  const { data: clients = [], isLoading } = useManagementClients();

  useEffect(() => {
    if (!isLoading && (!clients || clients.length <= 0)) {
      navigate("/");
    }
  }, [clients, isLoading, navigate]);

  const baseClientIds = clients.map((c) => c.clientId);

  const { data: smsAllMetrics, isLoading: isSmsAllMetricsLoading } = useGetMessagingMetrics(
    token,
    startDate,
    endDate,
    false,
    MessageDeliveryType.Text,
    selectedClient === "All" ? baseClientIds : [selectedClient],
  );

  const { data: smsErrors, isLoading: isSmsErrorsLoading } = useGetMessagingMetrics(
    token,
    startDate,
    endDate,
    true,
    MessageDeliveryType.Text,
    selectedClient === "All" ? baseClientIds : [selectedClient],
  );

  const { data: allEmails, isLoading: isAllEmailsLoading } = useGetMessagingMetrics(
    token,
    startDate,
    endDate,
    false,
    MessageDeliveryType.Email,
    selectedClient === "All" ? baseClientIds : [selectedClient],
  );

  const { data: emailErrors, isLoading: isEmailErrorsLoading } = useGetMessagingMetrics(
    token,
    startDate,
    endDate,
    true,
    MessageDeliveryType.Email,
    selectedClient === "All" ? baseClientIds : [selectedClient],
  );

  const { data: errorMessagesSms, isLoading: isErrorMessagesSms } = useGetMessagingErrors(
    token,
    startDate,
    endDate,
    MessageDeliveryType.Text,
    selectedClient === "All" ? baseClientIds : [selectedClient],
  );

  return (
    <PageContainer>
      <Breadcrumbs pageTitle="Messaging" />
      <PageTitle>Messaging</PageTitle>
      <Paper sx={{ mt: { xs: 1, md: 2 }, p: { xs: 2, lg: 4 } }}>
        <Stack direction="row" spacing={2} sx={{ p: 1 }}>
          <TextField
            id="client-select"
            select
            label="Select Client"
            defaultValue="All"
            fullWidth
            sx={{ width: 300 }}
            onChange={(ev) => setSelectedClient(ev.target.value)}
          >
            <MenuItem key={"All"} value={"All"}>
              All
            </MenuItem>
            {clients &&
              clients.map((option) => (
                <MenuItem key={option.clientId} value={option.clientId}>
                  {option.clientName}
                </MenuItem>
              ))}
          </TextField>
          <DateRange
            sx={{ width: 400 }}
            startDate={startDate}
            endDate={endDate}
            startDateOnChange={(d) => setStartDate(d ?? new Date())}
            endDateOnChange={(d) => setEndDate(d ?? new Date())}
          />
        </Stack>
        <Stack direction="column" spacing={2} height={2000} sx={{ mt: 3 }}>
          <Typography variant="h3">All SMS</Typography>
          {smsAllMetrics && !isSmsAllMetricsLoading ? createAreaChart(smsAllMetrics) : <LoadingSpinner />}
          <Typography variant="h3">SMS Errors</Typography>
          {smsErrors && !isSmsErrorsLoading ? createAreaChart(smsErrors) : <LoadingSpinner />}
          <Typography variant="h3">All Emails</Typography>
          {allEmails && !isAllEmailsLoading ? createAreaChart(allEmails) : <LoadingSpinner />}
          <Typography variant="h3">Email Errors</Typography>
          {emailErrors && !isEmailErrorsLoading ? createAreaChart(emailErrors) : <LoadingSpinner />}
          <Typography variant="h3">Top Errors</Typography>
          {errorMessagesSms && !isErrorMessagesSms ? BasicTable(errorMessagesSms) : <LoadingSpinner />}
        </Stack>
      </Paper>
    </PageContainer>
  );
}

function getRandomColor() {
  return COLORS[Math.floor(Math.random() * COLORS.length + 1)];
}

const createAreaChart = (metrics: MessagingMetrics[]) => {
  return (
    <ResponsiveContainer width="100%" maxHeight={300}>
      <AreaChart data={metrics} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          type="number"
          allowDuplicatedCategory={false}
          domain={["dataMin", "dataMax"]}
          tickFormatter={(t) => new Date(t * 1000).toLocaleDateString()}
        />
        <YAxis dataKey="value" type="number" domain={["auto", "dataMax"]} />
        <Tooltip labelFormatter={(t) => new Date(t * 1000).toLocaleDateString()} />
        {metrics?.map((c) => {
          const color = getRandomColor();
          return (
            <Area
              type="monotone"
              dataKey="value"
              data={c.clientMetrics}
              name={c.name}
              key={c.name}
              stroke={color}
              fillOpacity={0.35}
              fill={color}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

function BasicTable(errors: MessagingError[]) {
  return (
    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
      <Table sx={{ minWidth: 650 }} aria-label="error table">
        <TableHead>
          <TableRow>
            <TableCell>Count</TableCell>
            <TableCell>Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.map((err, idx) => (
            <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
              <TableCell component="th" scope="row">
                {err.count}
              </TableCell>
              <TableCell>{err.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
